import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import {
  PageContainer,
  PageHeader,
  PageMainCard,
  PageMainStack,
} from "@volca/ui/components/ui/page-components";
import { SearchInput } from "@volca/ui/components/ui/search-input";
import { useGetBusinessReviewAccess } from "@/business/query";
import { SettingsPageLoading } from "@/business-settings/common/settings-page-loading";
import {
  SETTINGS_PAGE_TITLES,
  SIDEBAR_MODES,
  SIDEBAR_SECTIONS,
} from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { dayjs } from "@/core/utils/date";
import { multiFieldSearch } from "@/core/utils/multi-field-search";
import { useGetBusinessReviews } from "@/reviews/query";
import type { ReviewWithSearchableFields } from "@/reviews/review";
import { ReviewsTable } from "./reviews-table";
import { REVIEW_SEARCH_FIELDS } from "./search-constants";

const ReviewsContent = () => {
  const { businessId } = useParams();
  const {
    data: reviews = [],
    isLoading,
    isError,
    refetch,
  } = useGetBusinessReviews(businessId);
  const {
    updateSidebar,
    state: { selectedItemId },
  } = useBusinessSettingsSidebar();
  const [searchValue, setSearchValue] = useState("");

  const sortedFilteredReviews = useMemo(() => {
    let filtered: ReviewWithSearchableFields[] = reviews;

    // Apply search filter using multi-field search
    if (searchValue) {
      filtered = multiFieldSearch(
        filtered,
        searchValue,
        REVIEW_SEARCH_FIELDS,
      ) as ReviewWithSearchableFields[];
    }

    // Sort by date, most recent first
    return filtered.sort((a, b) => {
      const dateA = a.external_created_at || a.created_at;
      const dateB = b.external_created_at || b.created_at;
      return dayjs(dateB).valueOf() - dayjs(dateA).valueOf();
    });
  }, [reviews, searchValue]);

  const handleRowClick = (review: ReviewWithSearchableFields) => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.reviews,
      mode: SIDEBAR_MODES.view,
      selectedItemId: review.id,
      isOpen: true,
    });
  };

  return (
    <PageContainer variant="workspace">
      <PageHeader title={SETTINGS_PAGE_TITLES.reviews} />
      <PageMainCard>
        <PageMainStack className="min-h-0 flex-1 pt-6">
          <SearchInput
            placeholder="Search five-star reviews"
            value={searchValue}
            onChange={setSearchValue}
          />

          <div className="flex min-h-0 w-full flex-1 flex-col overflow-auto">
            <ReviewsTable
              data={sortedFilteredReviews}
              isLoading={isLoading}
              isError={isError}
              onRetry={() => void refetch()}
              searchValue={searchValue}
              onClearSearch={() => setSearchValue("")}
              selectedRowId={selectedItemId}
              onRowClick={handleRowClick}
            />
          </div>
        </PageMainStack>
      </PageMainCard>
    </PageContainer>
  );
};

const Reviews = () => {
  const { businessId } = useParams();
  const { data: reviewAccessData, isLoading } =
    useGetBusinessReviewAccess(businessId);

  if (isLoading) {
    return <SettingsPageLoading title={SETTINGS_PAGE_TITLES.reviews} />;
  }

  if (!reviewAccessData?.has_review_access) {
    return <Navigate to="../" replace />;
  }

  return <ReviewsContent />;
};

export default Reviews;
